import React, { useState, useRef, useEffect } from "react";
import { useAppContext } from "../context/AppContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import {
  Upload,
  Code,
  FileText,
  Save,
  PlayCircle,
  Download,
  AlertCircle,
  CheckCircle,
  Copy,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Spinner } from "../components/ui/spinner";
import CodeEditor from "../components/StrategyGenerator/CodeEditor";
import PdfViewer from "../components/StrategyGenerator/PdfViewer";
import JsonViewer from "../components/StrategyGenerator/JsonViewer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SampleStrategyModal from "../components/StrategyGenerator/SampleStrategyModel";
import {
  extractOcrText,
  testGeneratorStrategy,
  saveStrategy,
} from "../services/api";
import EnhancedOcrViewer from "../components/ui/enhanced-ocr-text.viewer";

function StrategyGeneratorPage() {
  const { actions } = useAppContext();
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ocrText, setOcrText] = useState("");
  const [strategyCode, setStrategyCode] = useState("");
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [activeTab, setActiveTab] = useState("pdf");
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [strategyName, setStrategyName] = useState("");
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clear any error/success messages when component unmounts
  useEffect(() => {
    return () => {
      setError(null);
      setSuccess(null);
    };
  }, []);

  // Auto-switch to OCR tab after processing
  useEffect(() => {
    if (ocrText && loading === false) {
      setActiveTab("ocr");
    }
  }, [ocrText, loading]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        actions.setNotification("Only PDF files are allowed", "error");
        return;
      }

      setPdfFile(file);
      const fileUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(fileUrl);

      // Suggest a strategy name based on file name
      const baseName = file.name.replace(/\.pdf$/i, "");
      setStrategyName(`${baseName}ParsingStrategy`);

      // Reset other states
      setOcrText("");
      setInvoiceResult(null);
      setError(null);
      setSuccess(null);
    }
  };

  const handleExtractOcr = async () => {
    if (!pdfFile) {
      actions.setNotification("Please upload a PDF file first", "error");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Use the extractOcrText API method from the service
      const result = await extractOcrText(pdfFile);

      if (result && result.ocrText) {
        setOcrText(result.ocrText);
        setActiveTab("ocr");
      } else {
        throw new Error("No OCR text returned from server");
      }
    } catch (err) {
      console.error("Error extracting OCR text:", err);
      // Display the exact error message from the API
      const errorMessage = err.message || "Error extracting OCR text";
      setError(errorMessage);
      actions.setNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTestStrategy = async () => {
    if (!ocrText) {
      actions.setNotification("Please extract OCR text first", "error");
      return;
    }

    if (!strategyCode) {
      actions.setNotification("Please enter or load a strategy", "error");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Use the testGeneratorStrategy API method from the service
      const result = await testGeneratorStrategy(ocrText, strategyCode);

      if (result && result.result) {
        setInvoiceResult(result.result);
        setActiveTab("result");
        setSuccess("Strategy test successful!");
      } else {
        throw new Error("No result returned from server");
      }
    } catch (err) {
      console.error("Error testing strategy:", err);
      // Display the exact error message from the API
      const errorMessage = err.message || "Error testing strategy";
      setError(errorMessage);
      actions.setNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStrategy = async () => {
    if (!strategyCode) {
      actions.setNotification("Please enter a strategy code", "error");
      return;
    }

    if (!strategyName) {
      actions.setNotification("Please enter a strategy name", "error");
      return;
    }

    setSavingStrategy(true);
    setError(null);
    setSuccess(null);
    try {
      // Use the saveStrategy API method from the service
      const result = await saveStrategy(strategyName, strategyCode);

      if (result && result.message) {
        setSuccess(
          `Strategy saved successfully as ${result.strategyInfo.fileName}`
        );
        actions.setNotification("Strategy saved successfully", "success");
      } else {
        throw new Error("Failed to save strategy");
      }
    } catch (err) {
      console.error("Error saving strategy:", err);
      // Display the exact error message from the API
      const errorMessage = err.message || "Error saving strategy";
      setError(errorMessage);
      actions.setNotification(errorMessage, "error");
    } finally {
      setSavingStrategy(false);
    }
  };

  const handleCopyOcrText = () => {
    if (ocrText) {
      navigator.clipboard
        .writeText(ocrText)
        .then(() => {
          actions.setNotification("OCR text copied to clipboard", "success");
        })
        .catch((err) => {
          console.error("Could not copy text: ", err);
          actions.setNotification("Failed to copy OCR text", "error");
        });
    }
  };

  const handleSelectSampleStrategy = (code) => {
    setStrategyCode(code);
    setShowSampleModal(false);
    setError(null);
    setSuccess(null);
  };

  const handleDownloadJson = () => {
    if (!invoiceResult) return;

    const dataStr = JSON.stringify(invoiceResult, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(
      dataStr
    )}`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute(
      "download",
      `${strategyName || "invoice"}_result.json`
    );
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
  };

  const handleDownloadStrategyCode = () => {
    if (!strategyCode) return;

    const dataUri = `data:text/plain;charset=utf-8,${encodeURIComponent(
      strategyCode
    )}`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", `${strategyName || "strategy"}.cs`);
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Strategy Generator</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleDownloadStrategyCode}
              disabled={!strategyCode}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Strategy
            </Button>
            <Button variant="outline" onClick={() => setShowSampleModal(true)}>
              <Code className="h-4 w-4 mr-2" />
              Load Sample
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              className="mb-4 bg-green-50 border-green-200 text-green-800"
            >
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertTitle className="text-green-700">Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="mb-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current.click()}
                className="w-full md:w-auto"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload PDF
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />

              <Button
                onClick={handleExtractOcr}
                disabled={!pdfFile || loading}
                className="w-full md:w-auto"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Extract OCR
                  </>
                )}
              </Button>

              <Button
                onClick={handleTestStrategy}
                disabled={!ocrText || !strategyCode || loading}
                className="w-full md:w-auto"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Test Strategy
              </Button>
            </div>

            {pdfFile && (
              <div className="mt-2 text-sm text-gray-500">
                Selected file: {pdfFile.name}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
            <div className="space-y-2">
              <Label htmlFor="strategyName">Strategy Name</Label>
              <div className="flex space-x-2">
                <Input
                  id="strategyName"
                  value={strategyName}
                  onChange={(e) => setStrategyName(e.target.value)}
                  placeholder="Enter a name for this strategy..."
                  className="flex-1"
                />
                <Button
                  onClick={handleSaveStrategy}
                  disabled={!strategyCode || !strategyName || savingStrategy}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {savingStrategy ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Strategy
                </Button>
              </div>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-4"
          >
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="pdf">PDF Viewer</TabsTrigger>
              <TabsTrigger value="ocr">OCR Text</TabsTrigger>
              <TabsTrigger value="result">Result</TabsTrigger>
            </TabsList>

            {/* PDF Viewer Tab */}
            <TabsContent value="pdf" className="h-96">
              {pdfPreviewUrl ? (
                <PdfViewer fileUrl={pdfPreviewUrl} />
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50 rounded-md border border-dashed border-gray-300">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No PDF file selected
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Upload a PDF file to preview it here
                    </p>
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="mt-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Select File
                    </button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* OCR Text Tab */}
            <TabsContent value="ocr" className="h-96">
              <EnhancedOcrViewer
                text={ocrText}
                onChange={(e) => setOcrText(e.target.value)}
                readOnly={loading}
                onCopy={handleCopyOcrText}
              />
            </TabsContent>

            {/* Result Tab */}
            <TabsContent value="result" className="h-96">
              <JsonViewer
                data={invoiceResult}
                onDownload={handleDownloadJson}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Strategy Code</CardTitle>
          <p className="text-sm text-gray-500">
            Write or load a C# strategy that implements IInvoiceParsingStrategy
          </p>
        </CardHeader>
        <CardContent className="h-[600px]">
          <CodeEditor
            value={strategyCode}
            onChange={setStrategyCode}
            language="csharp"
          />
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => setShowSampleModal(true)}>
            <Code className="h-4 w-4 mr-2" />
            Load Sample Strategy
          </Button>
          <Button
            onClick={handleTestStrategy}
            disabled={!ocrText || !strategyCode || loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Testing...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 mr-2" />
                Test Strategy
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      <SampleStrategyModal
        isOpen={showSampleModal}
        onClose={() => setShowSampleModal(false)}
        onSelect={handleSelectSampleStrategy}
      />
    </div>
  );
}

export default StrategyGeneratorPage;
// import React, { useState, useRef, useEffect } from "react";
// import { useAppContext } from "../context/AppContext";
// import {
//   Card,
//   CardHeader,
//   CardTitle,
//   CardContent,
//   CardFooter,
// } from "../components/ui/card";
// import {
//   Tabs,
//   TabsContent,
//   TabsList,
//   TabsTrigger,
// } from "../components/ui/tabs";
// import { Button } from "../components/ui/button";
// import {
//   Upload,
//   Code,
//   FileText,
//   Save,
//   PlayCircle,
//   Download,
//   AlertCircle,
//   CheckCircle,
//   Copy,
// } from "lucide-react";
// import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
// import { Spinner } from "../components/ui/spinner";
// import CodeEditor from "../components/StrategyGenerator/CodeEditor";
// import PdfViewer from "../components/StrategyGenerator/PdfViewer";
// import JsonViewer from "../components/StrategyGenerator/JsonViewer";
// import { Input } from "../components/ui/input";
// import { Label } from "../components/ui/label";
// import SampleStrategyModal from "../components/StrategyGenerator/SampleStrategyModel";
// import {
//   extractOcrText,
//   testGeneratorStrategy,
//   saveStrategy,
// } from "../services/api";
// import EnhancedOcrViewer from "../components/ui/enhanced-ocr-text.viewer";

// function StrategyGeneratorPage() {
//   const { actions } = useAppContext();
//   const fileInputRef = useRef(null);
//   const [pdfFile, setPdfFile] = useState(null);
//   const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [ocrText, setOcrText] = useState("");
//   const [strategyCode, setStrategyCode] = useState("");
//   const [invoiceResult, setInvoiceResult] = useState(null);
//   const [activeTab, setActiveTab] = useState("pdf");
//   const [showSampleModal, setShowSampleModal] = useState(false);
//   const [strategyName, setStrategyName] = useState("");
//   const [savingStrategy, setSavingStrategy] = useState(false);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);

//   // Clear any error/success messages when component unmounts
//   useEffect(() => {
//     return () => {
//       setError(null);
//       setSuccess(null);
//     };
//   }, []);

//   // Auto-switch to OCR tab after processing
//   useEffect(() => {
//     if (ocrText && loading === false) {
//       setActiveTab("ocr");
//     }
//   }, [ocrText, loading]);

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       if (file.type !== "application/pdf") {
//         actions.setNotification("Only PDF files are allowed", "error");
//         return;
//       }

//       setPdfFile(file);
//       const fileUrl = URL.createObjectURL(file);
//       setPdfPreviewUrl(fileUrl);

//       // Suggest a strategy name based on file name
//       const baseName = file.name.replace(/\.pdf$/i, "");
//       setStrategyName(`${baseName}ParsingStrategy`);

//       // Reset other states
//       setOcrText("");
//       setInvoiceResult(null);
//     }
//   };

//   const handleExtractOcr = async () => {
//     if (!pdfFile) {
//       actions.setNotification("Please upload a PDF file first", "error");
//       return;
//     }

//     setLoading(true);
//     setError(null);
//     try {
//       // Use the extractOcrText API method from the service
//       const result = await extractOcrText(pdfFile);

//       if (result && result.ocrText) {
//         setOcrText(result.ocrText);
//         setActiveTab("ocr");
//       } else {
//         throw new Error("No OCR text returned from server");
//       }
//     } catch (err) {
//       console.error("Error extracting OCR text:", err);
//       setError(`Error extracting OCR text: ${err.message}`);
//       actions.setNotification(
//         `Error extracting OCR text: ${err.message}`,
//         "error"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleTestStrategy = async () => {
//     if (!ocrText) {
//       actions.setNotification("Please extract OCR text first", "error");
//       return;
//     }

//     if (!strategyCode) {
//       actions.setNotification("Please enter or load a strategy", "error");
//       return;
//     }

//     setLoading(true);
//     setError(null);
//     try {
//       // Use the testGeneratorStrategy API method from the service
//       const result = await testGeneratorStrategy(ocrText, strategyCode);

//       if (result && result.result) {
//         setInvoiceResult(result.result);
//         setActiveTab("result");
//         setSuccess("Strategy test successful!");
//       } else {
//         throw new Error("No result returned from server");
//       }
//     } catch (err) {
//       console.error("Error testing strategy:", err);
//       setError(`Error testing strategy: ${err.message}`);
//       actions.setNotification(
//         `Error testing strategy: ${err.message}`,
//         "error"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSaveStrategy = async () => {
//     if (!strategyCode) {
//       actions.setNotification("Please enter a strategy code", "error");
//       return;
//     }

//     if (!strategyName) {
//       actions.setNotification("Please enter a strategy name", "error");
//       return;
//     }

//     setSavingStrategy(true);
//     setError(null);
//     try {
//       // Use the saveStrategy API method from the service
//       const result = await saveStrategy(strategyName, strategyCode);

//       if (result && result.message) {
//         setSuccess(
//           `Strategy saved successfully as ${result.strategyInfo.fileName}`
//         );
//         actions.setNotification("Strategy saved successfully", "success");
//       } else {
//         throw new Error("Failed to save strategy");
//       }
//     } catch (err) {
//       console.error("Error saving strategy:", err);
//       setError(`Error saving strategy: ${err.message}`);
//       actions.setNotification(`Error saving strategy: ${err.message}`, "error");
//     } finally {
//       setSavingStrategy(false);
//     }
//   };

//   const handleCopyOcrText = () => {
//     if (ocrText) {
//       navigator.clipboard
//         .writeText(ocrText)
//         .then(() => {
//           actions.setNotification("OCR text copied to clipboard", "success");
//         })
//         .catch((err) => {
//           console.error("Could not copy text: ", err);
//           actions.setNotification("Failed to copy OCR text", "error");
//         });
//     }
//   };

//   const handleSelectSampleStrategy = (code) => {
//     setStrategyCode(code);
//     setShowSampleModal(false);
//   };

//   const handleDownloadJson = () => {
//     if (!invoiceResult) return;

//     const dataStr = JSON.stringify(invoiceResult, null, 2);
//     const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(
//       dataStr
//     )}`;

//     const linkElement = document.createElement("a");
//     linkElement.setAttribute("href", dataUri);
//     linkElement.setAttribute(
//       "download",
//       `${strategyName || "invoice"}_result.json`
//     );
//     document.body.appendChild(linkElement);
//     linkElement.click();
//     document.body.removeChild(linkElement);
//   };

//   const handleDownloadStrategyCode = () => {
//     if (!strategyCode) return;

//     const dataUri = `data:text/plain;charset=utf-8,${encodeURIComponent(
//       strategyCode
//     )}`;

//     const linkElement = document.createElement("a");
//     linkElement.setAttribute("href", dataUri);
//     linkElement.setAttribute("download", `${strategyName || "strategy"}.cs`);
//     document.body.appendChild(linkElement);
//     linkElement.click();
//     document.body.removeChild(linkElement);
//   };

//   return (
//     <div className="space-y-6">
//       <Card>
//         <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
//           <CardTitle>Strategy Generator</CardTitle>
//           <div className="flex space-x-2">
//             <Button
//               variant="outline"
//               onClick={handleDownloadStrategyCode}
//               disabled={!strategyCode}
//             >
//               <Download className="h-4 w-4 mr-2" />
//               Download Strategy
//             </Button>
//             <Button variant="outline" onClick={() => setShowSampleModal(true)}>
//               <Code className="h-4 w-4 mr-2" />
//               Load Sample
//             </Button>
//           </div>
//         </CardHeader>
//         <CardContent>
//           {error && (
//             <Alert variant="destructive" className="mb-4">
//               <AlertCircle className="h-4 w-4" />
//               <AlertTitle>Error</AlertTitle>
//               <AlertDescription>{error}</AlertDescription>
//             </Alert>
//           )}

//           {success && (
//             <Alert
//               variant="success"
//               className="mb-4 bg-green-50 border-green-200 text-green-800"
//             >
//               <CheckCircle className="h-4 w-4 text-green-500" />
//               <AlertTitle className="text-green-700">Success</AlertTitle>
//               <AlertDescription>{success}</AlertDescription>
//             </Alert>
//           )}

//           <div className="mb-4">
//             <div className="flex items-center space-x-4">
//               <Button
//                 variant="secondary"
//                 onClick={() => fileInputRef.current.click()}
//                 className="w-full md:w-auto"
//               >
//                 <Upload className="h-4 w-4 mr-2" />
//                 Upload PDF
//               </Button>
//               <input
//                 ref={fileInputRef}
//                 type="file"
//                 accept="application/pdf"
//                 onChange={handleFileChange}
//                 className="hidden"
//               />

//               <Button
//                 onClick={handleExtractOcr}
//                 disabled={!pdfFile || loading}
//                 className="w-full md:w-auto"
//               >
//                 {loading ? (
//                   <>
//                     <Spinner size="sm" className="mr-2" />
//                     Processing...
//                   </>
//                 ) : (
//                   <>
//                     <FileText className="h-4 w-4 mr-2" />
//                     Extract OCR
//                   </>
//                 )}
//               </Button>

//               <Button
//                 onClick={handleTestStrategy}
//                 disabled={!ocrText || !strategyCode || loading}
//                 className="w-full md:w-auto"
//               >
//                 <PlayCircle className="h-4 w-4 mr-2" />
//                 Test Strategy
//               </Button>
//             </div>

//             {pdfFile && (
//               <div className="mt-2 text-sm text-gray-500">
//                 Selected file: {pdfFile.name}
//               </div>
//             )}
//           </div>

//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
//             <div className="space-y-2">
//               <Label htmlFor="strategyName">Strategy Name</Label>
//               <div className="flex space-x-2">
//                 <Input
//                   id="strategyName"
//                   value={strategyName}
//                   onChange={(e) => setStrategyName(e.target.value)}
//                   placeholder="Enter a name for this strategy..."
//                   className="flex-1"
//                 />
//                 <Button
//                   onClick={handleSaveStrategy}
//                   disabled={!strategyCode || !strategyName || savingStrategy}
//                   className="bg-green-600 hover:bg-green-700 text-white"
//                 >
//                   {savingStrategy ? (
//                     <Spinner size="sm" className="mr-2" />
//                   ) : (
//                     <Save className="h-4 w-4 mr-2" />
//                   )}
//                   Save Strategy
//                 </Button>
//               </div>
//             </div>
//           </div>

//           <Tabs
//             value={activeTab}
//             onValueChange={setActiveTab}
//             className="space-y-4"
//           >
//             <TabsList className="grid grid-cols-3">
//               <TabsTrigger value="pdf">PDF Viewer</TabsTrigger>
//               <TabsTrigger value="ocr">OCR Text</TabsTrigger>
//               <TabsTrigger value="result">Result</TabsTrigger>
//             </TabsList>

//             {/* PDF Viewer Tab */}
//             <TabsContent value="pdf" className="h-96">
//               {pdfPreviewUrl ? (
//                 <PdfViewer fileUrl={pdfPreviewUrl} />
//               ) : (
//                 <div className="flex items-center justify-center h-full bg-gray-50 rounded-md border border-dashed border-gray-300">
//                   <div className="text-center">
//                     <Upload className="mx-auto h-12 w-12 text-gray-400" />
//                     <h3 className="mt-2 text-sm font-medium text-gray-900">
//                       No PDF file selected
//                     </h3>
//                     <p className="mt-1 text-sm text-gray-500">
//                       Upload a PDF file to preview it here
//                     </p>
//                     <button
//                       onClick={() => fileInputRef.current.click()}
//                       className="mt-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                     >
//                       Select File
//                     </button>
//                   </div>
//                 </div>
//               )}
//             </TabsContent>

//             {/* OCR Text Tab - Enhanced Version */}
//             <TabsContent value="ocr" className="h-96">
//               <EnhancedOcrViewer
//                 text={ocrText}
//                 onChange={(e) => setOcrText(e.target.value)}
//                 readOnly={loading}
//               />
//               {/* <div className="relative h-full">
//                 <textarea
//                   value={ocrText}
//                   onChange={(e) => setOcrText(e.target.value)}
//                   className="w-full h-full p-4 font-mono text-sm border rounded-md"
//                   placeholder="Extract OCR text from PDF..."
//                   readOnly={loading}
//                 />
//                 {ocrText && (
//                   <Button
//                     size="sm"
//                     variant="secondary"
//                     onClick={handleCopyOcrText}
//                     className="absolute top-2 right-2"
//                     title="Copy to clipboard"
//                   >
//                     <Copy className="h-4 w-4" />
//                   </Button>
//                 )}
//               </div> */}
//             </TabsContent>

//             {/* Result Tab */}
//             <TabsContent value="result" className="h-96">
//               <JsonViewer
//                 data={invoiceResult}
//                 onDownload={handleDownloadJson}
//               />
//             </TabsContent>
//           </Tabs>
//         </CardContent>
//       </Card>

//       <Card>
//         <CardHeader className="pb-2">
//           <CardTitle>Strategy Code</CardTitle>
//           <p className="text-sm text-gray-500">
//             Write or load a C# strategy that implements IInvoiceParsingStrategy
//           </p>
//         </CardHeader>
//         <CardContent className="h-[600px]">
//           <CodeEditor
//             value={strategyCode}
//             onChange={setStrategyCode}
//             language="csharp"
//           />
//         </CardContent>
//         <CardFooter className="flex justify-between">
//           <Button variant="outline" onClick={() => setShowSampleModal(true)}>
//             <Code className="h-4 w-4 mr-2" />
//             Load Sample Strategy
//           </Button>
//           <Button
//             onClick={handleTestStrategy}
//             disabled={!ocrText || !strategyCode || loading}
//           >
//             {loading ? (
//               <>
//                 <Spinner size="sm" className="mr-2" />
//                 Testing...
//               </>
//             ) : (
//               <>
//                 <PlayCircle className="h-4 w-4 mr-2" />
//                 Test Strategy
//               </>
//             )}
//           </Button>
//         </CardFooter>
//       </Card>

//       <SampleStrategyModal
//         isOpen={showSampleModal}
//         onClose={() => setShowSampleModal(false)}
//         onSelect={handleSelectSampleStrategy}
//       />
//     </div>
//   );
// }

// export default StrategyGeneratorPage;
